import React, { useState, useEffect } from 'react';
import axios from 'axios';
import toast from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';
import API_URL from '../config/api';
import {
  FiSearch,
  FiEye,
  FiEdit2,
  FiTrash2,
  FiFilter,
  FiChevronDown,
  FiChevronUp,
  FiHome,
  FiMapPin,
  FiUser,
  FiCalendar,
  FiAlertCircle,
  FiBriefcase,
  FiKey,
  FiCopy,
} from 'react-icons/fi';

const Clients = () => {
  const navigate = useNavigate();
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showFilters, setShowFilters] = useState(false);
  const [expandedClient, setExpandedClient] = useState(null);
  const [filters, setFilters] = useState({
    search: '',
    powerStatus: '',
    page: 1,
  });
  const [pagination, setPagination] = useState({
    total: 0,
    totalPages: 1,
    currentPage: 1,
  });

  useEffect(() => {
    fetchClients();
  }, [filters.page, filters.powerStatus]);

  const fetchClients = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        page: filters.page,
        limit: 10,
        ...(filters.powerStatus && { powerStatus: filters.powerStatus }),
        ...(filters.search && { search: filters.search }),
      });

      const response = await axios.get(`${API_URL}/api/clients?${params}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
      });

      setClients(response.data.clients || []);
      setPagination({
        total: response.data.total || 0,
        totalPages: response.data.totalPages || 1,
        currentPage: response.data.currentPage || 1,
      });
    } catch (error) {
      console.error('Error fetching clients:', error);
      toast.error('Error al cargar clientes');
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    fetchClients();
  };

  const clearFilters = () => {
    setFilters({
      search: '',
      powerStatus: '',
      page: 1,
    });
    setTimeout(() => fetchClients(), 100);
  };

  const toggleClientExpand = (clientId) => {
    setExpandedClient(expandedClient === clientId ? null : clientId);
  };

  const handleViewClient = (clientNumber) => {
    navigate(`/clients/${clientNumber}`);
    toast.success(`Viendo cliente #${clientNumber}`);
  };

  const handleEditClient = (clientNumber) => {
    navigate(`/clients/edit/${clientNumber}`);
    toast.success(`Editando cliente #${clientNumber}`);
  };

  const handleDeleteClient = async (clientNumber, clientName) => {
    if (window.confirm(`¿Estás seguro de eliminar al cliente ${clientName}?`)) {
      try {
        await axios.delete(`${API_URL}/api/clients/${clientNumber}`, {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
        });
        toast.success('Cliente eliminado correctamente');
        fetchClients(); // Recargar la lista
      } catch (error) {
        console.error('Error deleting client:', error);
        toast.error('Error al eliminar cliente');
      }
    }
  };

  const getPowerStatusColor = (status, urgent) => {
    if (urgent) return 'bg-red-600 text-white border-red-700 animate-pulse';
    switch (status) {
      case 'activo':
        return 'bg-green-100 text-green-800 border-green-200';
      case 'sin_luz':
        return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'desconectado':
        return 'bg-gray-100 text-gray-800 border-gray-200';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const getPowerStatusText = (status, urgent) => {
    if (urgent) return '¡URGENTE!';
    switch (status) {
      case 'activo':
        return 'Activo';
      case 'sin_luz':
        return 'Sin Luz';
      case 'desconectado':
        return 'Desconectado';
      default:
        return status;
    }
  };

  const getResidenceIcon = (type) => {
    switch (type) {
      case 'casa':
        return <FiHome className="mr-1" size={14} />;
      case 'apartamento':
        return <FiHome className="mr-1" size={14} />;
      default:
        return <FiMapPin className="mr-1" size={14} />;
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('es-ES');
  };

  const isDueDateNear = (dueDateString) => {
    if (!dueDateString) return false;
    const today = new Date();
    const due = new Date(dueDateString);
    const diffTime = due - today;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays <= 5 && diffDays >= 0;
  };

  if (loading && clients.length === 0) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="p-4 md:p-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4 mb-6">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-gray-800">
            Clientes
          </h1>
          <p className="text-sm md:text-base text-gray-600 mt-1">
            Gestiona todos tus clientes
          </p>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={clearFilters}
            className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800 border rounded-lg hover:bg-gray-50 transition-colors"
          >
            Limpiar filtros
          </button>
          <div className="bg-blue-100 text-blue-800 px-4 py-2 rounded-lg text-sm font-medium">
            Total: {pagination.total}
          </div>
        </div>
      </div>

      {/* Barra de búsqueda principal */}
      <div className="mb-4">
        <form onSubmit={handleSearch} className="relative">
          <FiSearch
            className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400"
            size={20}
          />
          <input
            type="text"
            placeholder="Buscar por nombre, compañía, dirección o ZIDCode..."
            value={filters.search}
            onChange={(e) => setFilters({ ...filters, search: e.target.value })}
            className="w-full pl-12 pr-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
          />
          <button
            type="submit"
            className="absolute right-2 top-1/2 transform -translate-y-1/2 px-4 py-1.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm"
          >
            Buscar
          </button>
        </form>
      </div>

      {/* Botón para mostrar/ocultar filtros avanzados */}
      <button
        onClick={() => setShowFilters(!showFilters)}
        className="flex items-center gap-2 text-sm text-gray-600 hover:text-gray-800 mb-4"
      >
        <FiFilter size={18} />
        Filtros avanzados
        {showFilters ? <FiChevronUp size={18} /> : <FiChevronDown size={18} />}
      </button>

      {/* Filtros avanzados (colapsables) */}
      {showFilters && (
        <div className="bg-white rounded-xl shadow p-4 mb-6 border border-gray-200">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Estado de la Luz
              </label>
              <select
                value={filters.powerStatus}
                onChange={(e) =>
                  setFilters({ ...filters, powerStatus: e.target.value, page: 1 })
                }
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              >
                <option value="">Todos los estados</option>
                <option value="activo">Activo</option>
                <option value="sin_luz">Sin Luz</option>
                <option value="desconectado">Desconectado</option>
              </select>
            </div>
          </div>
        </div>
      )}

      {/* Vista para móvil: Tarjetas */}
      <div className="md:hidden space-y-4">
        {clients.length === 0 ? (
          <div className="bg-white rounded-xl shadow p-8 text-center">
            <FiUser className="mx-auto text-4xl text-gray-400 mb-3" />
            <p className="text-gray-500">No hay clientes para mostrar</p>
            <p className="text-sm text-gray-400 mt-2">Prueba con otros filtros</p>
          </div>
        ) : (
          clients.map((client) => (
            <div
              key={client._id}
              className="bg-white rounded-xl shadow overflow-hidden border border-gray-200"
            >
              {/* Cabecera de la tarjeta - siempre visible */}
              <div
                className="p-4 flex items-center justify-between cursor-pointer hover:bg-gray-50 transition-colors"
                onClick={() => toggleClientExpand(client._id)}
              >
                <div className="flex items-center space-x-3">
                  <div
                    className={`w-10 h-10 rounded-full flex items-center justify-center text-white font-bold ${
                      client.urgent
                        ? 'bg-red-600'
                        : client.powerStatus === 'activo'
                        ? 'bg-green-600'
                        : client.powerStatus === 'sin_luz'
                        ? 'bg-yellow-600'
                        : 'bg-gray-600'
                    }`}
                  >
                    {client.fullName?.charAt(0).toUpperCase()}
                  </div>
                  <div>
                    <p className="font-semibold text-gray-900">
                      {client.fullName}
                    </p>
                    <p className="text-xs text-gray-500">
                      #{client.clientNumber} • {client.company || 'Sin compañía'}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <span
                    className={`px-2 py-1 text-xs rounded-full ${getPowerStatusColor(
                      client.powerStatus,
                      client.urgent
                    )}`}
                  >
                    {getPowerStatusText(client.powerStatus, client.urgent)}
                  </span>
                  {expandedClient === client._id ? (
                    <FiChevronUp size={20} />
                  ) : (
                    <FiChevronDown size={20} />
                  )}
                </div>
              </div>

              {/* Detalles expandibles */}
              {expandedClient === client._id && (
                <div className="px-4 pb-4 pt-2 border-t border-gray-100 bg-gray-50">
                  <div className="grid grid-cols-2 gap-3 text-sm">
                    {/* Compañía */}
                    <div className="bg-white p-3 rounded-lg col-span-2">
                      <p className="text-xs text-gray-500 mb-1 flex items-center">
                        <FiBriefcase className="mr-1" size={12} />
                        Compañía
                      </p>
                      <p className="font-medium text-gray-900">
                        {client.company || 'No especificada'}
                      </p>
                    </div>

                    {/* Credenciales */}
                    <div className="bg-white p-3 rounded-lg col-span-2">
                      <p className="text-xs text-gray-500 mb-1 flex items-center">
                        <FiKey className="mr-1" size={12} />
                        Credenciales
                      </p>
                      <div className="flex justify-between items-center">
                        <span className="text-sm">Usuario: {client.username || 'N/A'}</span>
                        <button
                          onClick={() => {
                            navigator.clipboard.writeText(client.username || '');
                            toast.success('Usuario copiado');
                          }}
                          className="text-blue-600 hover:text-blue-800"
                        >
                          <FiCopy size={14} />
                        </button>
                      </div>
                      <div className="flex justify-between items-center mt-1">
                        <span className="text-sm">Contraseña: {client.clientPassword ? '••••••••' : 'N/A'}</span>
                        <button
                          onClick={() => {
                            navigator.clipboard.writeText(client.clientPassword || '');
                            toast.success('Contraseña copiada');
                          }}
                          className="text-blue-600 hover:text-blue-800"
                        >
                          <FiCopy size={14} />
                        </button>
                      </div>
                    </div>

                    {/* Fechas */}
                    <div className="bg-white p-3 rounded-lg">
                      <p className="text-xs text-gray-500 mb-1 flex items-center">
                        <FiCalendar className="mr-1" size={12} />
                        Inicio
                      </p>
                      <p className="font-medium">{formatDate(client.startDate)}</p>
                    </div>
                    <div className="bg-white p-3 rounded-lg">
                      <p className="text-xs text-gray-500 mb-1 flex items-center">
                        <FiCalendar className="mr-1" size={12} />
                        Vencimiento
                      </p>
                      <p
                        className={`font-medium ${
                          isDueDateNear(client.dueDate) ? 'text-red-600 font-bold' : ''
                        }`}
                      >
                        {formatDate(client.dueDate)}
                        {isDueDateNear(client.dueDate) && (
                          <FiAlertCircle className="inline ml-1 text-red-600" size={14} />
                        )}
                      </p>
                    </div>

                    {/* Códigos */}
                    <div className="bg-white p-3 rounded-lg">
                      <p className="text-xs text-gray-500 mb-1">Código 2D</p>
                      <p className="font-mono">{client.twoDigitCode}</p>
                    </div>
                    <div className="bg-white p-3 rounded-lg">
                      <p className="text-xs text-gray-500 mb-1">ZIDCode</p>
                      <p className="font-mono">{client.zidCode || 'N/A'}</p>
                    </div>

                    {/* Dirección */}
                    <div className="col-span-2 bg-white p-3 rounded-lg">
                      <p className="text-xs text-gray-500 mb-1 flex items-center">
                        {getResidenceIcon(client.residenceType)}
                        Residencia / Dirección
                      </p>
                      <p className="text-gray-900 capitalize">{client.residenceType}</p>
                      <p className="text-sm text-gray-600 mt-1">{client.address}</p>
                      <p className="text-xs text-gray-500 mt-1">
                        Casilla: {client.twoDigitBox}
                      </p>
                    </div>

                    {/* Cancelación */}
                    {client.cancellationReason && (
                      <div className="col-span-2 bg-red-50 p-3 rounded-lg border border-red-200">
                        <p className="text-xs text-red-600 mb-1 font-semibold">
                          Cancelado - {client.cancellationType === 'falta_pago' ? 'Falta de pago' : 'Otro'}
                        </p>
                        <p className="text-sm text-gray-700">{client.cancellationReason}</p>
                        {client.cancellationNumber && (
                          <p className="text-xs text-gray-500 mt-1">
                            N° Cancelación: {client.cancellationNumber}
                          </p>
                        )}
                      </div>
                    )}

                    {/* Para traspaso */}
                    {client.forTransfer && (
                      <div className="col-span-2 bg-blue-50 p-3 rounded-lg border border-blue-200">
                        <p className="text-xs text-blue-600 font-semibold">
                          ⏳ Pendiente de traspaso
                        </p>
                      </div>
                    )}
                  </div>

                  {/* Acciones - FUNCIONALES */}
                  <div className="flex justify-end gap-3 mt-4 pt-2 border-t border-gray-200">
                    <button
                      onClick={() => handleViewClient(client.clientNumber)}
                      className="flex items-center gap-1 px-3 py-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors text-sm"
                      title="Ver detalles"
                    >
                      <FiEye size={16} />
                      <span>Ver</span>
                    </button>
                    <button
                      onClick={() => handleEditClient(client.clientNumber)}
                      className="flex items-center gap-1 px-3 py-2 text-green-600 hover:bg-green-50 rounded-lg transition-colors text-sm"
                      title="Editar cliente"
                    >
                      <FiEdit2 size={16} />
                      <span>Editar</span>
                    </button>
                    <button
                      onClick={() => handleDeleteClient(client.clientNumber, client.fullName)}
                      className="flex items-center gap-1 px-3 py-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors text-sm"
                      title="Eliminar cliente"
                    >
                      <FiTrash2 size={16} />
                      <span>Eliminar</span>
                    </button>
                  </div>
                </div>
              )}
            </div>
          ))
        )}
      </div>

      {/* Vista para desktop: Tabla */}
      <div className="hidden md:block bg-white rounded-xl shadow overflow-hidden border border-gray-200">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                N°
              </th>
              <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Cliente / Compañía
              </th>
              <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Usuario
              </th>
              <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Fechas
              </th>
              <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Códigos
              </th>
              <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Estado Luz
              </th>
              <th className="px-6 py-4 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                Acciones
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {clients.length === 0 ? (
              <tr>
                <td colSpan="7" className="px-6 py-12 text-center text-gray-500">
                  <FiUser className="mx-auto text-4xl text-gray-300 mb-3" />
                  <p>No hay clientes para mostrar</p>
                  <p className="text-sm text-gray-400 mt-2">
                    Prueba con otros filtros
                  </p>
                </td>
              </tr>
            ) : (
              clients.map((client) => (
                <tr key={client._id} className="hover:bg-gray-50 transition-colors">
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                    #{client.clientNumber}
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex items-center">
                      <div
                        className={`w-8 h-8 rounded-full flex items-center justify-center text-white text-xs font-bold mr-3 ${
                          client.urgent
                            ? 'bg-red-600'
                            : client.powerStatus === 'activo'
                            ? 'bg-green-600'
                            : client.powerStatus === 'sin_luz'
                            ? 'bg-yellow-600'
                            : 'bg-gray-600'
                        }`}
                      >
                        {client.fullName?.charAt(0).toUpperCase()}
                      </div>
                      <div>
                        <p className="text-sm font-medium text-gray-900">
                          {client.fullName}
                        </p>
                        <p className="text-xs text-gray-500">
                          {client.company || 'Sin compañía'}
                        </p>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <p className="text-sm text-gray-600">{client.username || 'N/A'}</p>
                    <p className="text-xs text-gray-400">
                      Pass: {client.clientPassword ? '••••' : 'N/A'}
                    </p>
                  </td>
                  <td className="px-6 py-4">
                    <p className="text-xs text-gray-600">
                      Inicio: {formatDate(client.startDate)}
                    </p>
                    <p
                      className={`text-xs font-medium ${
                        isDueDateNear(client.dueDate)
                          ? 'text-red-600'
                          : 'text-gray-600'
                      }`}
                    >
                      Vence: {formatDate(client.dueDate)}
                      {isDueDateNear(client.dueDate) && (
                        <FiAlertCircle className="inline ml-1 text-red-600" size={12} />
                      )}
                    </p>
                  </td>
                  <td className="px-6 py-4">
                    <p className="text-xs font-mono text-gray-600">
                      2D: {client.twoDigitCode}
                    </p>
                    <p className="text-xs font-mono text-gray-600">
                      ZID: {client.zidCode || 'N/A'}
                    </p>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span
                      className={`px-2 py-1 text-xs rounded-full ${getPowerStatusColor(
                        client.powerStatus,
                        client.urgent
                      )}`}
                    >
                      {getPowerStatusText(client.powerStatus, client.urgent)}
                    </span>
                    {client.forTransfer && (
                      <span className="ml-2 text-xs text-blue-600">(Traspaso)</span>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right space-x-2">
                    <button
                      onClick={() => handleViewClient(client.clientNumber)}
                      className="text-blue-600 hover:text-blue-800 p-1.5 rounded-lg hover:bg-blue-50 transition-colors"
                      title="Ver detalles"
                    >
                      <FiEye size={18} />
                    </button>
                    <button
                      onClick={() => handleEditClient(client.clientNumber)}
                      className="text-green-600 hover:text-green-800 p-1.5 rounded-lg hover:bg-green-50 transition-colors"
                      title="Editar cliente"
                    >
                      <FiEdit2 size={18} />
                    </button>
                    <button
                      onClick={() => handleDeleteClient(client.clientNumber, client.fullName)}
                      className="text-red-600 hover:text-red-800 p-1.5 rounded-lg hover:bg-red-50 transition-colors"
                      title="Eliminar cliente"
                    >
                      <FiTrash2 size={18} />
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Paginación */}
      {pagination.totalPages > 1 && (
        <div className="mt-6 flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4 bg-white rounded-xl shadow p-4 border border-gray-200">
          <div className="text-sm text-gray-600 text-center sm:text-left">
            Mostrando página {filters.page} de {pagination.totalPages} ·{' '}
            {pagination.total} clientes totales
          </div>
          <div className="flex justify-center gap-2">
            <button
              onClick={() => setFilters({ ...filters, page: filters.page - 1 })}
              disabled={filters.page === 1}
              className="px-4 py-2 border rounded-lg disabled:opacity-50 hover:bg-gray-50 transition-colors text-sm"
            >
              Anterior
            </button>
            <div className="flex items-center px-4 py-2 bg-blue-50 text-blue-700 rounded-lg text-sm font-medium">
              {filters.page}
            </div>
            <button
              onClick={() => setFilters({ ...filters, page: filters.page + 1 })}
              disabled={filters.page === pagination.totalPages}
              className="px-4 py-2 border rounded-lg disabled:opacity-50 hover:bg-gray-50 transition-colors text-sm"
            >
              Siguiente
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default Clients;